import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { faGear, faQuestionCircle, faUserGear } from '@fortawesome/pro-regular-svg-icons';
import { faMessage } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash-es';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';

import { trackEvent } from 'jf/analytics/Analytics';
import { Study, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { CircledFontAwesomeIcon } from 'jf/components/CircledFontAwesomeIcon';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExInput } from 'jf/components/DevExInput';
import { DevExText } from 'jf/components/DevExText';
import { EditableText } from 'jf/components/EditableText';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { formatAvgTimeSec } from 'jf/utils/timeUtils';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientPerms } from 'jf/utils/useClientPerms';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { SendStudyReminderModal } from '../SendStudyReminderModal';
import { useStudyParticipation } from '../useStudyParticipation';

import { ManageSlackTargetsModal } from './ManageSlackTargetsModal';
import { ParticipationChart } from './ParticipationChart';

const formatResponseRate = (responseRate: number) => {
  if (responseRate === 0) {
    return '—';
  }
  // A hand-entered target below the responses already collected would otherwise
  // render a nonsense figure such as 1200.0%.
  return `${(Math.min(responseRate, 1) * 100).toFixed(1)}%`;
};

type ParticipationStatProps = {
  label: ReactNode;
  stat: number | ReactNode | undefined;
};

const ParticipationStat: React.FC<ParticipationStatProps> = (props) => {
  const theme = useDevExTheme();

  return (
    <Flex vertical gap={theme.variable.spacing.sm}>
      <DevExText type="secondary">{props.label}</DevExText>
      <DevExText fontSize="xl" fontFamily="secondary" loading={!props.stat} loadingWidth={40}>
        {props.stat}
      </DevExText>
    </Flex>
  );
};

type ParticipationChartProps = {
  studyRef: string | undefined;
  teamRef?: string;
  openManageDrawer: () => void;
};

export const ParticipationCard: React.FC<ParticipationChartProps> = (props) => {
  const theme = useDevExTheme();
  const queryClient = useQueryClient();
  const flags = useClientFlags();
  const sendStudyReminderModal = useJFOverlay(SendStudyReminderModal);
  const manageSlackTargetsModal = useJFOverlay(ManageSlackTargetsModal); // deprecated for MS Teams

  const [targetResponses, setTargetResponses] = useState<number | null>(null);

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: props.studyRef });
  const { data: participation } = useStudyParticipation({
    studyRef: props.studyRef,
    teamRef: props.teamRef,
  });

  const { useGetStudyTargets, msgIcon } = useTargetingClient();
  const { data: targets } = useGetStudyTargets({
    studyRef: props.studyRef,
    isEnabled: flags.slackTargeting,
  });

  const messengerTargetingEnabled = !!targets?.length;

  const { mutateAsync: updateStudy } = useClientMutation(StudyClient.updateStudy);

  const isResponsesCountEditable =
    study && study.distributionMethod === Study.distributionMethod.MANUAL;

  const debounceUpdateTargetResponses = useMemo(
    () =>
      debounce((targetResponses: number | null) => {
        trackEvent('survey-analyzer:participation:set-target-responses', {
          targetResponses,
        });
        updateStudy({ studyRef: props.studyRef!, requestBody: { targetResponses } }).then(() =>
          queryClient.invalidateQueries(['GET_STUDY', props.studyRef])
        );
      }, 500),
    []
  );

  const onSendReminder = () => {
    trackEvent(`survey-analyzer:send-slack-reminder:open`);
    sendStudyReminderModal.open();
  };

  const onManageTargets = () => {
    trackEvent(`survey-analyzer:manage-slack-targets:open`);
    manageSlackTargetsModal.open();
  };

  useEffect(() => {
    if (study) {
      if (study.expectedResponseCount) {
        setTargetResponses(study.expectedResponseCount);
      } else if (!targetResponses && study.targetResponses) {
        setTargetResponses(study.targetResponses);
      }
    }
  }, [study]);

  let shareLink = '';

  if (participation?.responseTimes.length === 0) {
    shareLink = `${window.location.origin}/study/${props.studyRef}`;
  }

  const perms = useClientPerms();
  const timeToRespond: {
    unit: string;
    value: number | string;
  } = useMemo(() => {
    let unit = 'Days';
    let value: string | number = '-';
    if (!study?.scheduledCloseDate) {
      return {
        unit,
        value: 0,
      };
    }
    const close = DateTime.fromISO(study.scheduledCloseDate);
    const diff = close.diff(DateTime.now(), ['days', 'hours', 'minutes']).toObject();
    if (diff.days && diff.days >= 1) {
      value = Math.floor(diff.days);
    } else if (diff.hours && diff.hours >= 1) {
      unit = 'Hours';
      value = Math.floor(diff.hours);
    } else if (diff.minutes && diff.minutes >= 1) {
      unit = 'Minutes';
      value = Math.floor(diff.minutes);
    }
    return {
      unit,
      value,
    };
  }, [study]);
  return (
    <DevExCard
      title="Participation"
      extra={
        messengerTargetingEnabled &&
        study?.status === 'OPEN' &&
        !flags.msTeamsTargeting &&
        !flags.scheduledCloseReminders && ( // button is hidden in MS Teams targeting
          <Tooltip title="Manage Slack Recipients">
            <DevExButton
              icon={<FontAwesomeIcon icon={faUserGear} />}
              type="outline"
              onClick={onManageTargets}
            />
          </Tooltip>
        )
      }
    >
      <Flex gap={theme.variable.spacing.lg}>
        <ParticipationStat
          label="Responses"
          stat={participation?.responseTimes.length === 0 ? '—' : participation?.uniqueResponses}
        />
        <ParticipationStat
          label={'Total Recipients'}
          stat={
            !isResponsesCountEditable ? (
              targetResponses
            ) : (
              <EditableText
                value={targetResponses?.toString()}
                onChange={(value) => {
                  const newValue = value ? parseInt(value) : null;
                  setTargetResponses(newValue);
                  debounceUpdateTargetResponses(newValue);
                }}
                disabled={!perms.role.customerAdmin}
                placeholder="0"
                size="large"
                inputStyle={{ width: '80px' }}
              />
            )
          }
        />
        {/*
          Recipients are study-wide, so with a team selected this would divide that
          team's responses by the whole study's recipients. There is no per-team
          recipient count to divide by, so the stat is dropped rather than shown wrong.
        */}
        {!props.teamRef && (
          <ParticipationStat
            label={'Response Rate'}
            stat={
              participation &&
              study &&
              formatResponseRate(
                targetResponses ? participation.uniqueResponses / targetResponses : 0
              )
            }
          />
        )}
        <ParticipationStat
          label={
            <Tooltip title="Average response time includes numeric rating questions only.">
              <Flex gap={theme.variable.spacing.xs}>
                Average Response Time
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Flex>
            </Tooltip>
          }
          stat={participation && formatAvgTimeSec(participation.avgTimeSec)}
        />
        {!!study?.scheduledCloseDate && study.status === Study.status.OPEN && (
          <ParticipationStat
            label={
              <Tooltip
                title={`Survey will close ${DateTime.fromISO(study.scheduledCloseDate, { zone: 'utc' }).toFormat('cccc, MMM d, yyyy')}`}
              >
                {timeToRespond.unit} Left to Respond
              </Tooltip>
            }
            stat={timeToRespond.value}
          />
        )}
      </Flex>

      {participation?.responseTimes.length === 0 ? (
        <>
          <Flex vertical align="center" gap={theme.variable.spacing.md}>
            <CircledFontAwesomeIcon icon={faMessage} />
            <div style={{ fontSize: theme.variable.fontSize.xl }}>No responses... yet!</div>
          </Flex>
          {messengerTargetingEnabled ? (
            <Flex justify="center" gap={theme.variable.spacing.md}>
              {flags.scheduledCloseReminders ? (
                <DevExButton
                  type="secondary"
                  icon={<FontAwesomeIcon icon={faGear} />}
                  onClick={props.openManageDrawer}
                >
                  Manage
                </DevExButton>
              ) : (
                <DevExButton
                  type="outline"
                  icon={<FontAwesomeIcon icon={faUserGear} />}
                  onClick={onManageTargets}
                >
                  Manage respondents
                </DevExButton>
              )}

              {!flags.scheduledCloseReminders && (
                <DevExButton
                  type="primary"
                  icon={<FontAwesomeIcon icon={msgIcon} />}
                  onClick={onSendReminder}
                >
                  Send reminder
                </DevExButton>
              )}
            </Flex>
          ) : (
            <Flex vertical align="center" gap={theme.variable.spacing.md}>
              <div>Copy the survey link below and share it with your recipients.</div>
              <Flex gap={theme.variable.spacing.sm}>
                <DevExInput value={shareLink} style={{ width: 500 }} />
                <DevExButton
                  type="primary"
                  copyValue={shareLink}
                  onClick={() =>
                    trackEvent('survey-analyzer:share-survey:copy', { surveyRef: props.studyRef })
                  }
                >
                  Copy
                </DevExButton>
              </Flex>
            </Flex>
          )}
        </>
      ) : (
        <ParticipationChart studyRef={props.studyRef} teamRef={props.teamRef} />
      )}
    </DevExCard>
  );
};
